import { useCallback } from 'react';
import { supabase } from '../integrations/supabase/client';

export const useAuthActions = () => {
  const signUp = useCallback(async (email: string, password: string, userData: any = {}) => {
    try {
      console.log('Starting signup process for:', email);
      
      // Set email redirect URL for confirmation
      const emailRedirectTo = userData.emailRedirectTo || `${window.location.origin}/auth/confirm`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo,
          data: {
            full_name: userData.full_name || '',
            user_type: userData.user_type || 'renter',
            referred_by: userData.referred_by || null,
            payment_status: userData.payment_status || 'none',
            payment_required: userData.payment_required || false
          }
        }
      });

      if (error) {
        console.error('Signup error:', error);
        return { error };
      }

      // Store new signup flag for onboarding
      localStorage.setItem('flamingo_new_signup', 'true');
      
      console.log('Signup successful:', data);
      return { data, error: null };
    } catch (error) {
      console.error('Signup exception:', error);
      return { error };
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      console.log('Starting signin process for:', email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Signin error:', error);
        return { error };
      }

      // Clear new signup flag on successful login
      localStorage.removeItem('flamingo_new_signup');
      
      console.log('Signin successful:', data);
      return { data, error: null };
    } catch (error) {
      console.error('Signin exception:', error);
      return { error };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      console.log('Starting logout process');
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Logout error:', error);
        throw error;
      }

      // Clear all local storage items
      localStorage.removeItem('flamingo_new_signup');
      localStorage.removeItem('flamingo_onboarding_complete');
      
      console.log('Logout successful');
    } catch (error) {
      console.error('Logout exception:', error);
      throw error;
    }
  }, []);

  const updateProfile = useCallback(async (data: any, userId: string, fetchProfile: (id: string) => void) => {
    try {
      console.log('Updating profile for user:', userId);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Profile update error:', error);
        return { error };
      }

      // Refresh profile data
      fetchProfile(userId);
      
      console.log('Profile updated successfully');
      return { error: null };
    } catch (error) {
      console.error('Profile update exception:', error);
      return { error };
    }
  }, []);

  const checkPaymentStatus = useCallback(async (userId: string) => {
    try {
      console.log('Checking payment status for user:', userId);
      
      // Get current profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('payment_status, payment_required, user_type, subscription_tier')
        .eq('id', userId)
        .single();

      if (profileError) {
        console.error('Error fetching profile for payment check:', profileError);
        return { paymentRequired: false, error: profileError };
      }

      const isLandlord = profile.user_type === 'landlord';
      const hasPendingPayment = profile.payment_status === 'pending';
      const requiresPayment = profile.payment_required && profile.payment_status !== 'completed';

      console.log('Payment status check result:', {
        isLandlord,
        paymentStatus: profile.payment_status,
        paymentRequired: profile.payment_required,
        requiresPayment
      });

      // If landlord without completed payment, they need to pay
      if (isLandlord && requiresPayment) {
        return { paymentRequired: true, error: null };
      }

      // For renters or landlords with completed payment
      return { paymentRequired: false, error: null };
    } catch (error) {
      console.error('Payment status check exception:', error);
      return { paymentRequired: false, error };
    }
  }, []);

  const activateAccount = useCallback(async (userId: string) => {
    try {
      console.log('Activating account for user:', userId);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          payment_status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Account activation error:', error);
        return { error };
      }

      console.log('Account activated successfully');
      return { error: null };
    } catch (error) {
      console.error('Account activation exception:', error);
      return { error };
    }
  }, []);

  return {
    signUp,
    signIn,
    logout,
    updateProfile,
    checkPaymentStatus,
    activateAccount
  };
};